import {
  App,
  Button,
  DatePicker,
  Empty,
  Form,
  Input,
  Modal,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  EditOutlined,
  PlusOutlined,
  StopOutlined,
  SwapOutlined,
  TableOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import dayjs, { type Dayjs } from 'dayjs';
import { InquiryFunnel } from './inquiry-funnel';
import {
  frontDeskApi,
  type Inquiry,
  type InquiryInput,
  type InquirySource,
  type InquiryStatus,
} from '@/api/frontdesk';
import { setupApi } from '@/api/setup';
import { studentsApi } from '@/api/students';
import { hrApi } from '@/api/hr';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const SOURCE_LABEL: Record<InquirySource, string> = {
  WALK_IN: 'Walk-in',
  REFERRAL: 'Referral',
  PHONE: 'Phone',
  OTHER: 'Other',
};
const STATUS_COLOR: Record<InquiryStatus, string> = {
  NEW: 'processing',
  CONTACTED: 'cyan',
  VISIT_SCHEDULED: 'gold',
  ENROLLED: 'success',
  DROPPED: 'default',
};
const STATUS_LABEL: Record<InquiryStatus, string> = {
  NEW: 'New',
  CONTACTED: 'Contacted',
  VISIT_SCHEDULED: 'Visit scheduled',
  ENROLLED: 'Enrolled',
  DROPPED: 'Dropped',
};

interface FormShape {
  studentName: string;
  classInterestedId?: number;
  guardianName?: string;
  guardianPhone?: string;
  source: InquirySource;
  assignedToStaffId?: number;
  inquiryDate: Dayjs;
  remarks?: string;
}

export function InquiriesPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('FRONT_DESK:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('FRONT_DESK:EDIT'));
  const canRegister = useAuthStore((s) => s.hasAuthority('STUDENT_MGMT:CREATE'));

  const [searchParams, setSearchParams] = useSearchParams();
  const view = (searchParams.get('view') === 'funnel' ? 'funnel' : 'list') as 'list' | 'funnel';
  const setView = (next: 'list' | 'funnel') => {
    const sp = new URLSearchParams(searchParams);
    if (next === 'list') sp.delete('view');
    else sp.set('view', 'funnel');
    setSearchParams(sp, { replace: true });
  };

  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(60, 'day'), dayjs()]);
  const [statusFilter, setStatusFilter] = useState<InquiryStatus | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Inquiry | null>(null);
  const [creating, setCreating] = useState(false);
  const [convertingId, setConvertingId] = useState<number | null>(null);
  const [form] = Form.useForm<FormShape>();

  const params = {
    from: range[0].format('YYYY-MM-DD'),
    to: range[1].format('YYYY-MM-DD'),
    status: statusFilter,
    q: search.trim() || undefined,
  };

  const { data, isFetching } = useQuery({
    queryKey: ['frontdesk', 'inquiries', params],
    queryFn: () => frontDeskApi.listInquiries(params),
  });

  const { data: classes } = useQuery({
    queryKey: ['setup', 'classes'],
    queryFn: setupApi.listClasses,
    staleTime: 5 * 60_000,
  });

  const { data: staffPage } = useQuery({
    queryKey: ['hr', 'staff', 'active', 'all'],
    queryFn: () => hrApi.searchStaff({ active: true, size: 500 }),
    staleTime: 60_000,
  });
  const staff = staffPage?.rows ?? [];

  const upsert = useMutation({
    mutationFn: (values: FormShape) => {
      const body: InquiryInput = {
        studentName: values.studentName.trim(),
        classInterestedId: values.classInterestedId ?? null,
        guardianName: values.guardianName?.trim() || undefined,
        guardianPhone: values.guardianPhone?.trim() || undefined,
        source: values.source,
        assignedToStaffId: values.assignedToStaffId ?? null,
        inquiryDate: values.inquiryDate.format('YYYY-MM-DD'),
        remarks: values.remarks?.trim() || undefined,
      };
      return editing ? frontDeskApi.updateInquiry(editing.id, body) : frontDeskApi.createInquiry(body);
    },
    onSuccess: () => {
      message.success(editing ? 'Inquiry updated' : 'Inquiry recorded');
      qc.invalidateQueries({ queryKey: ['frontdesk', 'inquiries'] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: InquiryStatus }) =>
      frontDeskApi.updateInquiryStatus(id, { followUpStatus: status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['frontdesk', 'inquiries'] }),
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({ source: 'WALK_IN', inquiryDate: dayjs() });
  };
  const openEdit = (row: Inquiry) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({
      studentName: row.studentName,
      classInterestedId: row.classInterestedId ?? undefined,
      guardianName: row.guardianName ?? undefined,
      guardianPhone: row.guardianPhone ?? undefined,
      source: row.source,
      assignedToStaffId: row.assignedToStaffId ?? undefined,
      inquiryDate: dayjs(row.inquiryDate),
      remarks: row.remarks ?? undefined,
    });
  };
  const open = creating || editing != null;

  const columns: TableProps<Inquiry>['columns'] = [
    {
      title: 'Date',
      dataIndex: 'inquiryDate',
      width: 110,
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    {
      title: 'Student',
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{row.studentName}</Typography.Text>
          {row.classInterestedName && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              for {row.classInterestedName}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Guardian',
      width: 220,
      render: (_, row) =>
        row.guardianName || row.guardianPhone ? (
          <Space orientation="vertical" size={0}>
            {row.guardianName && <Typography.Text>{row.guardianName}</Typography.Text>}
            {row.guardianPhone && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.guardianPhone}
              </Typography.Text>
            )}
          </Space>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Source',
      dataIndex: 'source',
      width: 110,
      render: (s: InquirySource) => <Tag>{SOURCE_LABEL[s]}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'followUpStatus',
      width: 170,
      render: (s: InquiryStatus, row) =>
        row.convertedStudentId ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            Enrolled · {row.convertedStudentRegistrationNumber}
          </Tag>
        ) : (
          <Tag color={STATUS_COLOR[s]}>{STATUS_LABEL[s]}</Tag>
        ),
    },
    {
      title: 'Assigned',
      dataIndex: 'assignedToStaffName',
      width: 160,
      render: (n: string | null) => n ?? <span style={{ opacity: 0.4 }}>—</span>,
    },
    {
      title: 'Actions',
      width: 220,
      align: 'right',
      render: (_, row) => {
        if (!canEdit) return null;
        if (row.convertedStudentId) {
          return (
            <Tooltip title="Inquiry already converted to enrollment">
              <Button size="small" type="text" disabled icon={<CheckCircleOutlined />}>
                Done
              </Button>
            </Tooltip>
          );
        }
        return (
          <Space size={4}>
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)}>
              Edit
            </Button>
            {canRegister && (
              <Tooltip title="Open student registration with this inquiry's details prefilled">
                <Button
                  size="small"
                  type="text"
                  icon={<UserAddOutlined />}
                  onClick={() => navigate(`/students/new?fromInquiry=${row.id}`)}
                >
                  Register
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Link to an existing student record">
              <Button
                size="small"
                type="text"
                icon={<SwapOutlined />}
                onClick={() => setConvertingId(row.id)}
              >
                Link
              </Button>
            </Tooltip>
            {row.followUpStatus !== 'DROPPED' && (
              <Tooltip title="Mark as dropped (won't pursue)">
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<StopOutlined />}
                  onClick={() => updateStatus.mutate({ id: row.id, status: 'DROPPED' })}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Front Desk"
        title="Admission inquiries"
        description="Walk-ins, calls, and referrals — track follow-ups through to enrollment."
        count={data ? { value: data.length, label: 'in range' } : undefined}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New inquiry
            </Button>
          ) : null
        }
      />

      <Space wrap size={12} style={{ alignItems: 'center' }}>
        <Segmented<'list' | 'funnel'>
          value={view}
          onChange={(v) => setView(v as 'list' | 'funnel')}
          options={[
            { value: 'list',   icon: <TableOutlined />,     label: 'List' },
            { value: 'funnel', icon: <AppstoreOutlined />,  label: 'Funnel' },
          ]}
        />
        <DatePicker.RangePicker
          value={range}
          onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])}
          format="DD MMM YYYY"
          allowClear={false}
        />
        {view === 'list' && (
          <Select
            allowClear
            placeholder="Status"
            style={{ minWidth: 180 }}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
            options={(Object.keys(STATUS_LABEL) as InquiryStatus[]).map((k) => ({
              value: k,
              label: STATUS_LABEL[k],
            }))}
          />
        )}
        <Input.Search
          allowClear
          placeholder="Search name / guardian / phone"
          style={{ width: 260 }}
          onSearch={(v) => setSearch(v)}
        />
      </Space>

      {data && data.length === 0 && !isFetching ? (
        <Empty description="No inquiries in this view" />
      ) : view === 'funnel' ? (
        <InquiryFunnel
          rows={data ?? []}
          loading={isFetching}
          onEdit={openEdit}
          onAdvance={(id, status) => updateStatus.mutate({ id, status })}
          onDrop={(id) => updateStatus.mutate({ id, status: 'DROPPED' })}
        />
      ) : (
        <Table
          rowKey="id"
          size="middle"
          loading={isFetching}
          columns={columns}
          dataSource={data ?? []}
          pagination={{ pageSize: 25 }}
        />
      )}

      <Modal
        title={editing ? `Edit inquiry · ${editing.studentName}` : 'New inquiry'}
        open={open}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText={editing ? 'Save' : 'Create'}
        destroyOnHidden
        width={560}
      >
        {open && (
          <Form form={form} layout="vertical" onFinish={(v) => upsert.mutate(v)}>
            <Form.Item label="Student name" name="studentName" rules={[{ required: true, max: 200 }]}>
              <Input autoFocus placeholder="Prospective student's name" />
            </Form.Item>
            <Form.Item label="Class interested in (optional)" name="classInterestedId">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
              />
            </Form.Item>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                label="Guardian"
                name="guardianName"
                style={{ flex: 1 }}
                rules={[{ max: 200 }]}
              >
                <Input placeholder="Father / mother / guardian" />
              </Form.Item>
              <Form.Item
                label="Phone"
                name="guardianPhone"
                style={{ width: 200 }}
                rules={[{ max: 20 }]}
              >
                <Input placeholder="03xx-xxxxxxx" />
              </Form.Item>
            </Space.Compact>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                label="Source"
                name="source"
                rules={[{ required: true }]}
                style={{ width: 200 }}
              >
                <Select
                  options={(Object.keys(SOURCE_LABEL) as InquirySource[]).map((k) => ({
                    value: k,
                    label: SOURCE_LABEL[k],
                  }))}
                />
              </Form.Item>
              <Form.Item
                label="Inquiry date"
                name="inquiryDate"
                rules={[{ required: true }]}
                style={{ flex: 1 }}
              >
                <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
              </Form.Item>
            </Space.Compact>
            <Form.Item label="Assigned to (optional)" name="assignedToStaffId">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={staff.map((s) => ({ value: s.id, label: `${s.firstName} ${s.lastName}` }))}
              />
            </Form.Item>
            <Form.Item label="Remarks" name="remarks" rules={[{ max: 2000 }]}>
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} placeholder="Notes from the conversation" />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {convertingId != null && (
        <ConvertModal id={convertingId} onClose={() => setConvertingId(null)} />
      )}
    </div>
  );
}

function ConvertModal({ id, onClose }: { id: number; onClose: () => void }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [studentId, setStudentId] = useState<number | undefined>(undefined);
  const [search, setSearch] = useState('');

  const { data: students, isFetching } = useQuery({
    queryKey: ['students', 'search', search],
    queryFn: () => studentsApi.search({ q: search.trim() || undefined, size: 20 }),
    enabled: search.trim().length > 0,
  });

  const convert = useMutation({
    mutationFn: () => frontDeskApi.convertInquiry(id, { studentId: studentId! }),
    onSuccess: () => {
      message.success('Inquiry marked as enrolled');
      qc.invalidateQueries({ queryKey: ['frontdesk', 'inquiries'] });
      onClose();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const options =
    students?.content.map((s) => ({
      value: s.id,
      label: `${s.firstName} ${s.lastName} · ${s.registrationNumber}`,
    })) ?? [];

  return (
    <Modal
      open
      title="Convert inquiry to enrollment"
      onCancel={onClose}
      onOk={() => convert.mutate()}
      okText="Mark enrolled"
      okButtonProps={{ disabled: !studentId, loading: convert.isPending }}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">
        Pick the student record this inquiry led to. Create the student first under{' '}
        <b>Students → New</b> if they aren't on file yet.
      </Typography.Paragraph>
      <Select
        showSearch
        style={{ width: '100%' }}
        placeholder="Search by name or registration number"
        loading={isFetching}
        filterOption={false}
        onSearch={(v) => setSearch(v)}
        notFoundContent={search.trim() === '' ? 'Type to search…' : 'No matches'}
        options={options}
        value={studentId}
        onChange={(v) => setStudentId(v)}
      />
    </Modal>
  );
}
